import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { useLogout } from '../hooks/useLogout';
import { useAuthContext } from '../hooks/useAuthContext';
import { useNavJoin } from '../utils/navJoin';
import './NavBar.css';

const Navbar = ({ isTransparent = false, hideTitle = false, fadeOnScroll = false, communityTag = null, isBlack = false, shouldHide = false, forceDarkText = false }) => {
  const { logout } = useLogout();
  // On a community's page, for non-members: its Join (utils/navJoin.js).
  const navJoin = useNavJoin();
  const { user } = useAuthContext();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // /respond and /ProfilePreview are public token-based pages a logged-out
  // recipient lands on directly from an email — "Search"/"Create
  // Community" aren't relevant prompts for someone who isn't even a member
  // yet and is just here to respond to a suggestion.
  const isPublicTokenRoute = location.pathname.toLowerCase() === '/respond' ||
                             location.pathname.toLowerCase() === '/profilepreview';
  const publicToken = isPublicTokenRoute ? searchParams.get('token') : null;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const [isV4, setIsV4] = useState(() => document.body.classList.contains('community-v4-layout'));
  const [v4Title, setV4Title] = useState(() => document.body.dataset.v4Title || '');
  const [v4CommunityId, setV4CommunityId] = useState(() => document.body.dataset.v4CommunityId || '');
  const [showManager, setShowManager] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsV4(document.body.classList.contains('community-v4-layout'));
      setV4Title(document.body.dataset.v4Title || '');
      setV4CommunityId(document.body.dataset.v4CommunityId || '');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-v4-title', 'data-v4-community-id'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const checkManagerVisibility = () => {
      if (localStorage.getItem('cb_has_communities')) {
        setShowManager(true);
        return;
      }
      try {
        const raw = localStorage.getItem('cb_anon_draft');
        if (raw) {
          const draft = JSON.parse(raw);
          const TTL = 60 * 60 * 1000;
          if (draft?.generated && Date.now() - draft.savedAt < TTL) {
            setShowManager(true);
            return;
          }
        }
      } catch (_) {}
      setShowManager(false);
    };
    checkManagerVisibility();
    window.addEventListener('storage', checkManagerVisibility);
    return () => window.removeEventListener('storage', checkManagerVisibility);
  }, [user]);

  const handleClick = () => {
    logout();
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.classList.add('body-menu-active');
    } else {
      document.body.classList.remove('body-menu-active');
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.classList.remove('body-menu-active');
  };

  // Handle scroll fade effect
  useEffect(() => {
    if (!fadeOnScroll) return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const bodyScrollTop = document.body.scrollTop;
      const fadeStart = 0;
      const fadeEnd = 100;
      
      let opacity = 1;
      const actualScrollTop = Math.max(scrollTop, bodyScrollTop);
      if (actualScrollTop > fadeStart) {
        opacity = Math.max(0, 1 - (actualScrollTop - fadeStart) / (fadeEnd - fadeStart));
      }
      
      setScrollOpacity(opacity);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    document.body.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, [fadeOnScroll]);

return (
  <>
    <header 
      id="navBarHeader" 
      className={`${isTransparent ? 'navbar-transparent' : ''} ${isBlack ? 'navbar-black' : ''} ${shouldHide ? 'navbar-hidden' : ''} ${forceDarkText ? 'navbar-text-dark' : ''} ${isPublicTokenRoute ? 'navbar-flat-bg' : ''}`}
      style={{
        ...(fadeOnScroll ? { opacity: scrollOpacity } : {}),
        ...(shouldHide ? { display: 'none !important' } : {})
      }}
    >
        <div className="navTitle">
          {isV4 && v4Title ? (
            <h1 id="navbar-title">{v4Title}</h1>
          ) : (
            <Link to="/" onClick={closeMenu}>
              <h1 id="navbar-title">{hideTitle ? '' : 'Interconnector'}</h1>
            </Link>
          )}
          <nav>
            <div className="menu-icon" onClick={toggleMenu}>
              {isMenuOpen ? null : <FaBars />}
            </div>
            <div className={`menu-active ${isMenuOpen ? "show" : "hide"}`}>
              <FaTimes className="close-icon" onClick={closeMenu} />
                        {user ? (
              <>
                {isV4 && (
                  <div className="navButton" onClick={closeMenu}>
                    <span className="navButtonText"><Link to="/">Home</Link></span>
                  </div>
                )}
                <div className="navButton" onClick={closeMenu}>
                  <span className="navButtonText"><Link to="/ProfileUpdate">Profile</Link></span>
                </div>
                <div className="navButton" onClick={closeMenu}>
                  <span className="navButtonText"><Link to="/messenger">Messenger</Link></span>
                </div>
                <div className="navButton" onClick={closeMenu}>
                  <span className="navButtonText"><Link to="/settings">Settings</Link></span>
                </div>
                {!isV4 && navJoin && (
                  <div className="navButton" onClick={() => { closeMenu(); navJoin(); }}>
                    <span className="navButtonText join-button_nav" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') { closeMenu(); navJoin(); } }}>Join</span>
                  </div>
                )}
                {!isV4 && (
                  <div className="navButton" onClick={closeMenu}>
                    <span className="navButtonText"><Link to="/groupsearch">Search</Link></span>
                  </div>
                )}
                {!isV4 && (
                  <div className="navButton" onClick={closeMenu}>
                    <span className="navButtonText"><Link to="/create">Create Community</Link></span>
                  </div>
                )}
                {!isV4 && showManager && (
                  <div className="navButton" onClick={closeMenu}>
                    <span className="navButtonText"><Link to="/community-manager">Manager</Link></span>
                  </div>
                )}
                <div className="navButton" onClick={() => { handleClick(); closeMenu(); }}>
                  <span className="navButtonText">Log out</span>
                </div>
              </>
            ) : (
              <>
                {isV4 && (
                  <div className="navButton" onClick={closeMenu}>
                    <span className="navButtonText"><Link to="/">Home</Link></span>
                  </div>
                )}
                {!isV4 && !isPublicTokenRoute && navJoin && (
                  <div className="navButton" onClick={() => { closeMenu(); navJoin(); }}>
                    <span className="navButtonText join-button_nav" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') { closeMenu(); navJoin(); } }}>Join</span>
                  </div>
                )}
                {!isV4 && !isPublicTokenRoute && (
                  <div className="navButton" onClick={closeMenu}>
                    <span className="navButtonText"><Link to="/groupsearch">Search</Link></span>
                  </div>
                )}
                {!isV4 && !isPublicTokenRoute && (
                  <div className="navButton" onClick={closeMenu}>
                    <span className="navButtonText"><Link to="/create">Create Community</Link></span>
                  </div>
                )}
                {isV4 && v4CommunityId && (
                  <div className="navButton" onClick={closeMenu}>
                    <span className="navButtonText"><Link to={`/demo?community=${v4CommunityId}`}>Join</Link></span>
                  </div>
                )}
                {isPublicTokenRoute && publicToken && (
                  <div className="navButton" onClick={closeMenu}>
                    <span className="navButtonText"><Link to={`/ProfilePreview?token=${encodeURIComponent(publicToken)}`}>Your Profile</Link></span>
                  </div>
                )}
                <div className="navButton" onClick={closeMenu}>
                  <span className="navButtonText"><Link to="/login">Login</Link></span>
                </div>
              </>
            )}


            </div>
          </nav>
        </div>
      </header>
      {/* Community tag positioned below navbar */}
      {communityTag && (
        <div className="navbar-community-tag" style={fadeOnScroll ? { opacity: scrollOpacity } : {}}>
          {communityTag}
        </div>
      )}
    </>
  );
};

export default Navbar;